/**
 * HistoryTab - Activity timeline derived from evidence dates.
 * Shows when this person appeared in conversations over time.
 */

import React from 'react';
import { Box, Typography, CircularProgress, Alert, Button, Divider } from '@mui/material';
import usePersonProfile from '../../hooks/usePersonProfile';
import Sparkline from '../Charts/Sparkline';

// eslint-disable-next-line max-lines-per-function
function HistoryTab({ node, entityId }) {
  const { evidence, loading, error, retry } = usePersonProfile(entityId, node.id);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={retry}>Retry</Button>}
        >
          Failed to load history
        </Alert>
      </Box>
    );
  }

  // Derive timeline from evidence dates
  const dateGroups = {};
  evidence.forEach((chunk) => {
    const date = chunk.created_at || chunk.timestamp;
    if (!date) return;
    const key = new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    if (!dateGroups[key]) {
      dateGroups[key] = [];
    }
    dateGroups[key].push(chunk);
  });

  const sortedDates = Object.keys(dateGroups).sort(
    (a, b) => new Date(b) - new Date(a)
  );

  // Sparkline data: evidence count per month (last 6 months)
  const monthCounts = getMonthlyActivity(evidence);

  return (
    <Box sx={{ p: 2 }}>
      {/* Activity Sparkline */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Activity Over Time
      </Typography>
      {monthCounts.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          <Sparkline data={monthCounts} width={360} height={60} color="#23A38F" />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            {monthCounts.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary">
                  {monthCounts[0].label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {monthCounts[monthCounts.length - 1].label}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No temporal data available
        </Typography>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Date-based Timeline */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Evidence Timeline ({evidence.length} items)
      </Typography>

      {sortedDates.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No dated evidence found
        </Typography>
      ) : (
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {sortedDates.slice(0, 20).map((date) => (
            <Box key={date} sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {date}
              </Typography>
              {dateGroups[date].slice(0, 3).map((chunk, i) => (
                <Box
                  key={`${date}-${i}`}
                  sx={{
                    pl: 2,
                    py: 0.5,
                    borderLeft: 2,
                    borderColor: '#23A38F',
                    ml: 1,
                    mt: 0.5,
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {truncateText(chunk.content || chunk.text || 'Evidence chunk', 120)}
                  </Typography>
                  {chunk.source_id && (
                    <Typography variant="caption" color="text.secondary">
                      Source: {chunk.source_id}
                    </Typography>
                  )}
                </Box>
              ))}
              {dateGroups[date].length > 3 && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 3 }}>
                  +{dateGroups[date].length - 3} more
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Summary */}
      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          First seen: {node.first_seen ? new Date(node.first_seen).toLocaleDateString() : 'Unknown'}
          {' | '}
          Last seen: {node.last_seen ? new Date(node.last_seen).toLocaleDateString() : 'Unknown'}
          {' | '}
          Total evidence: {evidence.length}
        </Typography>
      </Box>
    </Box>
  );
}

function getMonthlyActivity(evidence) {
  const months = {};
  evidence.forEach((chunk) => {
    const date = chunk.created_at || chunk.timestamp;
    if (!date) return;
    const d = new Date(date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    if (!months[key]) {
      months[key] = { key, label, value: 0 };
    }
    months[key].value++;
  });

  return Object.values(months)
    .sort((a, b) => a.key.localeCompare(b.key))
    .slice(-6);
}

function truncateText(text, maxLen) {
  if (text.length <= maxLen) return text;
  return text.substring(0, maxLen) + '...';
}

export default HistoryTab;
